import * as React from 'react';
import { Control, Errors } from 'react-redux-form';
import { FormWrapper } from '@common/components';
import { RouteComponentProps } from 'react-router';
import { CustomerViewModel } from '@models/Customer';
import { parsePhoneNumber } from '@common/parsers';
import {
  getDefaultErrorMessages,
  hasAlpha,
  hasValue,
  hasNumber,
  isValidEmail,
  isValidPhoneNumber,
  isValidCity,
  isValidZip,
  isValidCountry,
  isValidStateProvince
} from '@common/validators';
import { AddressViewModel } from '@models/common.models';
import { Select, CheckboxInline, Breadcrumbs } from '@common/components';
import { DynamicSelectIdentifier } from '@common/components/dynamic-select/dynamic-select-types';
import { Role, Roles } from '@models/Roles';
import FormNames from '@constants/FormNames.enum';

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface CustomerComponentProperties extends RouteComponentProps<{ id?: number }> {
  readonly message: string;
  readonly onSaveClick: (values: CustomerViewModel) => void;
  readonly getCustomer: (id: number) => void;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly resetForm: () => void;
  readonly isModal: boolean;
  readonly addresses?: Array<AddressViewModel>;
  readonly addAddressClick: (address?: AddressViewModel) => void;
  readonly formName: string;
  readonly shouldFormReset: boolean;
  readonly removeAddressClick: (index: number) => void;
  readonly setAddressOnePrimary: () => void;
  readonly entityId?: number;
  readonly saving?: boolean;
  readonly customerForm: CustomerViewModel;
  readonly propertyAddress?: AddressViewModel;
}

enum CustomerType {
  FNHW = 6
}

const checkPhone1Type = (values: CustomerViewModel) => {
  if (values) {
    if (!values.phone) {
      return true;
    }
    return values.phone1Type && values.phone1Type.id > 0 ? hasValue(values.phone1Type.id.toString()) : false;
  }

  return false;
};

const checkPhone2Type = (values: CustomerViewModel) => {
  if (values) {
    if (!values.phone2) {
      return true;
    }
    return values.phone2Type && values.phone2Type.id > 0 ? hasValue(values.phone2Type.id.toString()) : false;
  }
  return false;
};

interface CustomerComponentState {
  internationalAddresses: Array<number>;
  customerTypeId: number;
  validatePrimaryPhoneType: boolean;
  validateSecondaryPhoneType: boolean;
}

const initialState = {
  internationalAddresses: [],
  customerTypeId: 0,
  validatePrimaryPhoneType: false,
  validateSecondaryPhoneType: false
};

// Component definition
export class CustomerComponent extends React.Component<CustomerComponentProperties, CustomerComponentState> {
  state: CustomerComponentState = initialState;

  componentDidMount() {
    if (this.props.match && this.props.match.params.id) {
      this.props.getCustomer(this.props.match.params.id);
    } else if (this.props.entityId) {
      this.props.getCustomer(this.props.entityId);
    } else {
      this.props.resetForm();
    }
    this.handleAddressInit(this.props);
  }

  handleAddressInit = (newProps: CustomerComponentProperties) => {
    let internationalAddresses: number[] = [];
    if (newProps.addresses && newProps.addresses.length) {
      newProps.addresses.forEach((address: AddressViewModel, index: number) => {
        if (address.international) {
          internationalAddresses.push(index);
        }
      });
    }
    if ((newProps.match && newProps.match.params.id) || newProps.entityId) {
      this.setState(() => {
        return { internationalAddresses };
      });
    }
  };

  componentWillReceiveProps(newProps: CustomerComponentProperties) {
    if (
      this.props.addresses &&
      this.props.addresses.length > 0 &&
      this.props.addresses[0] &&
      this.props.addresses[0].line1 &&
      newProps.addresses &&
      this.props.addresses !== newProps.addresses
    ) {
      this.handleAddressInit(newProps);
    }
  }

  handleUpdates = (newModel: CustomerViewModel) => {
    if (newModel.customerType && newModel.customerType.id) {
      this.setState(prevState => {
        return {
          ...prevState,
          customerTypeId: +newModel.customerType.id,
          validatePrimaryPhoneType: !!newModel.phone,
          validateSecondaryPhonetype: !!newModel.phone2
        };
      });
    }
  };

  handleInternationalUpdate = (addrIndex: number): void | undefined => {
    this.setState(prevState => {
      let newInternationalAddresses;
      if (prevState.internationalAddresses) {
        if (prevState.internationalAddresses.indexOf(addrIndex) > -1) {
          // remove it
          newInternationalAddresses = prevState.internationalAddresses.filter(index => index !== addrIndex);
        } else {
          newInternationalAddresses = [...prevState.internationalAddresses, addrIndex];
        }

        return {
          internationalAddresses: newInternationalAddresses
        };
      }
      return { internationalAddresses: [addrIndex] };
    });
  };

  handleRemoveAddress = (index: number) => (e: React.MouseEvent<HTMLElement>) => {
    this.props.removeAddressClick(index);
  };

  setMailingAddress = (index: number) => (event: React.SyntheticEvent<HTMLInputElement>) => {
    if (
      index === 0 &&
      this.props.addresses &&
      this.props.addresses.length === 1 &&
      this.props.addresses[0].primary === false
    ) {
      this.props.setAddressOnePrimary();
    }
  };

  renderAddress(addresses: Array<AddressViewModel>) {
    if (!(addresses.length > 0)) {
      return;
    }
    return addresses.map((address, index) => {
      return (
        <div key={index} className="pt-10">
          <div className="dark-gray-bc p-10">
            <div className="row">
              <div className="col-md-11">
                <b>Mailing Address</b>
              </div>
              <div className="col-md-1 text-right">
                <i className="fa fa-times pointer" onClick={this.handleRemoveAddress(index)} />
              </div>
            </div>
            <div className="row">
              <div className="form-control col-md-6">
                <label htmlFor={FormNames.CustomerForm + `.addresses[${index}].line1`}>Line 1</label>
                <Control.text
                  onChange={this.setMailingAddress(index)}
                  model={`.addresses[${index}].line1`}
                  maxLength={32}
                  validators={{
                    hasAlpha: hasAlpha,
                    hasNumber,
                    required: hasValue
                  }}
                />
                <Errors
                  className="error"
                  model={`.addresses[${index}].line1`}
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
              <div className="form-control col-md-6">
                <label htmlFor={FormNames.CustomerForm + `.addresses[${index}].line2`}>Line 2 (optional)</label>
                <Control.text model={`.addresses[${index}].line2`} maxLength={32} />
                <Errors
                  className="error"
                  model={`.addresses[${index}].line2`}
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
            </div>
            <div className="row">
              <div className="form-control col-md-4">
                <label htmlFor={FormNames.CustomerForm + `.addresses[${index}].city`}>City</label>
                <Control.text
                  model={`.addresses[${index}].city`}
                  maxLength={28}
                  validators={{
                    isValidCity,
                    required: hasValue
                  }}
                />
                <Errors
                  className="error"
                  model={`.addresses[${index}].city`}
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
              <div className="form-control col-md-4">
                {this.state.internationalAddresses && this.state.internationalAddresses.indexOf(index) > -1 ? (
                  <>
                    <label htmlFor={FormNames.CustomerForm + `.addresses[${index}].state.abbreviation`}>
                      State/Province/Territory
                    </label>
                    <Control.text
                      maxLength={8}
                      model={`.addresses[${index}].state.abbreviation`}
                      validators={{
                        isValidStateProvince: isValidStateProvince,
                        required: hasValue
                      }}
                    />
                    <Errors
                      className="error"
                      model={`.addresses[${index}].state.abbreviation`}
                      show={{ touched: true, focus: false }}
                      messages={getDefaultErrorMessages()}
                    />
                  </>
                ) : (
                  <>
                    <label htmlFor={FormNames.CustomerForm + `.addresses[${index}].state.abbreviation`}>State</label>
                    <Select
                      model={`.addresses[${index}].state.abbreviation`}
                      identifier={DynamicSelectIdentifier.USStates}
                      validators={{
                        required: hasValue
                      }}
                    />
                    <Errors
                      className="error"
                      model={`.addresses[${index}].state.abbreviation`}
                      show={{ touched: true, focus: false }}
                      messages={getDefaultErrorMessages()}
                    />
                  </>
                )}
              </div>
              {this.state.internationalAddresses && this.state.internationalAddresses.indexOf(index) > -1 ? (
                <div className="form-control col-md-4">
                  <label htmlFor={FormNames.CustomerForm + `.addresses[${index}].country`}>Country</label>
                  <Control.text
                    maxLength={75}
                    model={`.addresses[${index}].country`}
                    validators={{
                      required: hasValue,
                      isValidCountry: isValidCountry
                    }}
                  />
                  <Errors
                    className="error"
                    model={`.addresses[${index}].country`}
                    show={{ touched: true, focus: false }}
                    messages={getDefaultErrorMessages()}
                  />
                </div>
              ) : (
                ''
              )}
              <div className="form-control col-md-2">
                {this.state.internationalAddresses && this.state.internationalAddresses.indexOf(index) > -1 ? (
                  <>
                    <label htmlFor={FormNames.CustomerForm + `.addresses[${index}].zip`}>Postal Code</label>
                    <Control.text
                      maxLength={10}
                      model={`.addresses[${index}].zip`}
                      validators={{
                        required: hasValue
                      }}
                    />
                    <Errors
                      className="error"
                      model={`.addresses[${index}].zip`}
                      show={{ touched: true, focus: false }}
                      messages={getDefaultErrorMessages()}
                    />
                  </>
                ) : (
                  <>
                    <label htmlFor={FormNames.CustomerForm + `.addresses[${index}].zip`}>Zip</label>
                    <Control.text
                      model={`.addresses[${index}].zip`}
                      maxLength={5}
                      validators={{
                        isValidZip: isValidZip,
                        required: hasValue
                      }}
                    />
                    <Errors
                      className="error"
                      model={`.addresses[${index}].zip`}
                      show={{ touched: true, focus: false }}
                      messages={getDefaultErrorMessages()}
                    />
                  </>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-md-4">
                <CheckboxInline>
                  <Control.checkbox
                    model={`.addresses[${index}].international`}
                    onChange={event => this.handleInternationalUpdate(index)}
                  />
                  <label
                    className="checkbox-inline"
                    htmlFor={FormNames.CustomerForm + `.addresses[${index}].international`}
                  >
                    International Address
                  </label>
                </CheckboxInline>
              </div>
              <div className="col-md-4">
                <CheckboxInline>
                  <Control.checkbox model={`.addresses[${index}].primary`} />
                  <label className="checkbox-inline" htmlFor={FormNames.CustomerForm + `.addresses[${index}].primary`}>
                    Mailing Address
                  </label>
                </CheckboxInline>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  renderHideSave() {
    if (this.props.isModal) {
      return (
        <div className="col-md-12">
          <div className="row end-xs">
            <button className="btn btn-hidden" type="submit" disabled={this.props.saving} ref={this.props.buttonRef}>
              {this.props.saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="col-md-12">
        <div className="row end-xs mt-10 mb-10">
          <button className="btn btn-secondary" type="button" onClick={() => window.history.back()}>
            Cancel
          </button>
          <button className="btn btn-primary" type="submit" disabled={this.props.saving} ref={this.props.buttonRef}>
            {this.props.saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  newPropertyAddressClick = () => {
    this.props.addAddressClick(this.props.propertyAddress);
  };

  newAddressClick = () => {
    this.props.addAddressClick();
  };

  render() {
    return (
      <>
        {!this.props.isModal ? <Breadcrumbs /> : null}
        <FormWrapper
          model={FormNames.CustomerForm}
          shouldFormReset={true}
          className="form-horizontal"
          hideNativeErrors={true}
          onChange={this.handleUpdates}
          onSubmit={this.props.onSaveClick}
          validators={{
            '': {
              hasPhone1Type: checkPhone1Type,
              hasPhone2Type: checkPhone2Type
            }
          }}
          validateOn="change"
        >
          <h2>Customer Detail</h2>
          <div className="row">
            <div className="form-control col-md-6">
              <label htmlFor={FormNames.CustomerForm + '.name'}>Name</label>
              <Control.text model=".name" maxLength={40} validators={{ required: hasValue, hasAlpha: hasAlpha }} />
              <Errors
                className="error"
                model=".name"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-3">
              <label htmlFor={FormNames.CustomerForm + '.emailAddress'}>Email Address</label>
              <Control.text model=".emailAddress" maxLength={50} validators={{ isValidEmail: isValidEmail }} />
              <Errors
                className="error"
                model=".emailAddress"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-control col-md-3">
              <label htmlFor={FormNames.CustomerForm + '.customerType.id'}>Customer Type</label>
              <Select
                model=".customerType.id"
                identifier={DynamicSelectIdentifier.CustomerTypes}
                validators={{ required: hasValue }}
              />
              <Errors
                className="error"
                model=".customerType.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-3">
              <label htmlFor={FormNames.CustomerForm + '.customerStatus.id'}>Customer Status</label>
              <Select
                model=".customerStatus.id"
                identifier={DynamicSelectIdentifier.CustomerStatuses}
                validators={{ required: hasValue }}
                disabled={!Role.CheckRoles([Roles.Admin, ...Role.GetAllIBS()])}
              />
              <Errors
                className="error"
                model=".customerStatus.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-3">
              <label htmlFor={FormNames.CustomerForm + '.language.id'}>Language</label>
              <Select
                model=".language.id"
                identifier={DynamicSelectIdentifier.CustomerLanguages}
                validators={{ required: hasValue }}
              />
              <Errors
                className="error"
                model=".language.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-3" hidden={this.state.customerTypeId !== CustomerType.FNHW}>
              <label htmlFor={FormNames.CustomerForm + '.company.id'}>Company</label>
              <Select model=".company.id" identifier={DynamicSelectIdentifier.Companies} />
            </div>
          </div>
          <div className="row">
            <div className="form-control col-md-3">
              <label htmlFor={FormNames.CustomerForm + '.phone'}>Primary Phone</label>
              <Control.text
                model=".phone"
                parser={parsePhoneNumber}
                validators={{
                  isValidPhoneNumber: isValidPhoneNumber
                }}
              />
              <Errors
                className="error"
                model=".phone"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-3">
              <label htmlFor={FormNames.CustomerForm + '.phone1Type.id'}>Primary Phone Type</label>
              <Select
                model=".phone1Type.id"
                identifier={DynamicSelectIdentifier.PhoneTypes}
                validator={
                  this.state.validatePrimaryPhoneType
                    ? {
                        hasValue
                      }
                    : null
                }
              />
              <Errors
                className="error"
                model={FormNames.CustomerForm}
                show={true}
                messages={getDefaultErrorMessages({
                  hasPhone1Type: 'Please select Primary Phone Type.'
                })}
              />
            </div>
            <div className="form-control col-md-3">
              <label htmlFor={FormNames.CustomerForm + '.phone2'}>Secondary Phone</label>
              <Control.text
                model=".phone2"
                parser={parsePhoneNumber}
                validators={{ isValidPhoneNumber: isValidPhoneNumber }}
              />
              <Errors
                className="error"
                model=".phone2"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-3">
              <label htmlFor={FormNames.CustomerForm + '.phone2Type.id'}>Secondary Phone Type</label>
              <Select
                model=".phone2Type.id"
                identifier={DynamicSelectIdentifier.PhoneTypes}
                validator={
                  this.state.validateSecondaryPhoneType
                    ? {
                        hasValue
                      }
                    : null
                }
              />
              <Errors
                className="error"
                model={FormNames.CustomerForm}
                show={true}
                messages={getDefaultErrorMessages({
                  hasPhone2Type: 'Please select Secondary Phone Type.'
                })}
              />
            </div>
            <div className="form-control col-md-3">
              <label>Contact Preference</label>
              <div className="row mt-10">
                <div className="col-md-6">
                  <CheckboxInline>
                    <Control.checkbox model=".usps" />
                    <label className="checkbox-inline" htmlFor={FormNames.CustomerForm + '.usps'}>
                      USPS
                    </label>
                  </CheckboxInline>
                </div>
                <div className="col-md-6">
                  {/* <label className="checkbox-inline" htmlFor="customerForm.fax"><Control.checkbox model=".fax" /> Fax</label> */}
                  <CheckboxInline>
                    <Control.checkbox model=".email" />
                    <label className="checkbox-inline" htmlFor={FormNames.CustomerForm + '.email'}>
                      Email
                    </label>
                  </CheckboxInline>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className="row middle-xs">
                <div className="col-md-6">
                  <CheckboxInline>
                    <Control.checkbox model=".isAgent" />
                    <label className="checkbox-inline" htmlFor={FormNames.CustomerForm + '.isAgent'}>
                      Is Real Estate Agent
                    </label>
                  </CheckboxInline>
                </div>
                <div className="col-md-6">
                  <div className="form-control">
                    <label htmlFor={FormNames.CustomerForm + '.agentId'}>Agent Id</label>
                    <Control.text model=".agentId" maxLength={64} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <h3>Address</h3>
            </div>
          </div>
          {this.props.customerForm.addresses &&
            this.props.customerForm.addresses.length > 0 &&
            this.renderAddress(this.props.customerForm.addresses)}
          <div className="row">
            <div className="col-md-12 mt-10">
              <button className="btn btn-secondary" type="button" onClick={this.newAddressClick}>
                Add Address
              </button>
              {this.props.isModal && (
                <button className="btn btn-secondary" type="button" onClick={this.newPropertyAddressClick}>
                  Copy Property Address
                </button>
              )}
            </div>
          </div>
          <div className="form-group">{this.renderHideSave()}</div>
        </FormWrapper>
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/customer/_components/customer/customer.component.tsx